import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { paymentsApi, type Payment } from '../features/payments/api.ts';
import { downloadPdf } from '../features/documents/api.ts';
import { MarkPaidDialog } from '../components/MarkPaidDialog.tsx';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;

function fmtMvr(n: number | null | undefined): string {
  if (n == null) return '—';
  return `MVR ${n.toFixed(2)}`;
}

function StatusPill({ paid }: { paid: boolean }): JSX.Element {
  return (
    <span className={paid ? 'pill pill-success' : 'pill pill-pending'}>
      {paid ? t.payments.statusPaid : t.payments.statusPending}
    </span>
  );
}

function DocumentsButton({ requestId, refNumber }: { requestId: string; refNumber: string }): JSX.Element {
  const [dl, setDl] = useState(false);
  async function handle() {
    setDl(true);
    try {
      await downloadPdf(paymentsApi.packageUrl(requestId), `payment-${refNumber}.pdf`);
    } finally {
      setDl(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void handle()}
      disabled={dl}
      className="btn-secondary btn-icon"
      title={dl ? t.payments.opening : t.payments.documentsTitle}
      aria-label={t.payments.documentsTitle}
    >
      <Icon name="description" size={18} />
    </button>
  );
}

function PaidToggle({ requestId, refNumber, paid }: { requestId: string; refNumber: string; paid: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Un-marking is a direct action; marking paid opens the payment-docs dialog.
  const unmarkMutation = useMutation({
    mutationFn: () => paymentsApi.setPaid(requestId, false),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['payments'] }),
  });
  // The mark-paid label is a full sentence in Dhivehi, which stretched the row
  // into a text slab. It moves to the tooltip/aria-label so the control stays a
  // button; the status pill beside it already states the state in words.
  const label = unmarkMutation.isPending
    ? t.payments.updating
    : paid ? t.payments.markUnpaid : t.payments.markPaid;
  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => (paid ? unmarkMutation.mutate() : setDialogOpen(true))}
        disabled={unmarkMutation.isPending}
        className={`btn-icon ${paid ? 'btn-secondary' : 'btn-primary'}`}
        title={label}
        aria-label={label}
      >
        <Icon name={paid ? 'restart_alt' : 'check'} size={18} />
      </button>
      {unmarkMutation.isError && (
        <span className="text-xs text-destructive">{t.payments.updateFailed}</span>
      )}
      {!paid && (
        <MarkPaidDialog requestId={requestId} refNumber={refNumber} open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </div>
  );
}

function Refs({ p }: { p: Payment }): JSX.Element {
  if (p.gsrNumber == null && p.prNumber == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="mono-meta text-muted-foreground">
      {p.gsrNumber != null && (
        <>{t.payments.gsrLabel} <span className="num">{p.gsrNumber}</span></>
      )}
      {p.gsrNumber != null && p.prNumber != null && ' · '}
      {p.prNumber != null && (
        <>{t.payments.prLabel} <span className="num">{p.prNumber}</span></>
      )}
    </span>
  );
}

type PaidFilter = 'all' | 'pending' | 'paid';

export function Payments(): JSX.Element {
  const { data: payments = [], isLoading, isError } = useQuery({
    queryKey: ['payments'],
    queryFn: () => paymentsApi.list(),
  });

  // Default to the "pending" (to-be-paid) view — that's the finance work queue.
  const [paidFilter, setPaidFilter] = useState<PaidFilter>('pending');
  const [yearFilter, setYearFilter] = useState<string>('all');

  // Distinct years present (by sent-to-finance date), newest first.
  const years = useMemo(
    () => Array.from(new Set(payments.map((p) => new Date(p.sentToFinanceAt).getFullYear()))).sort((a, b) => b - a),
    [payments],
  );

  // Title/ref/vendor search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [paidFilter, yearFilter, debouncedSearch]);

  const filtered = useMemo(() => payments.filter((p) => {
    if (paidFilter === 'pending' && p.paid) return false;
    if (paidFilter === 'paid' && !p.paid) return false;
    if (yearFilter !== 'all' && new Date(p.sentToFinanceAt).getFullYear() !== Number(yearFilter)) return false;
    if (debouncedSearch) {
      const hay = `${p.title} ${p.refNumber} ${p.winnerVendorName}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [payments, paidFilter, yearFilter, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const paidTabs: { key: PaidFilter; label: string }[] = [
    { key: 'all', label: t.payments.filterAll },
    { key: 'pending', label: t.payments.statusPending },
    { key: 'paid', label: t.payments.statusPaid },
  ];

  return (
    <div className="page space-y-6">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl tracking-tight">{t.payments.title}</h1>
        <p className="mono-meta text-muted-foreground">{t.payments.subtitle}</p>
      </header>

      {/* Toolbar — M3 segmented paid/pending filter + year selector on one side,
          search on the other, on the tinted chrome ground like WapMeet's list. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={t.payments.title}
          >
            {paidTabs.map((tab, i) => {
              const active = paidFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPaidFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {tab.label}
                </button>
              );
            })}
          </div>
          <select
            aria-label={t.payments.yearLabel}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="select w-auto rounded-full py-1.5"
          >
            <option value="all">{t.payments.allYears}</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.payments.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{t.payments.loading}</p>
      ) : isError ? (
        <p className="card-paper p-6 text-center text-sm text-destructive">{t.payments.loadError}</p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((p) => (
            <div
              key={p.requestId}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{p.title}</span>
                  <span className="mono-meta num text-muted-foreground">{p.refNumber}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="person" size={15} className="shrink-0 text-foreground/40" />
                    {p.winnerVendorName}
                  </span>
                  <span className="num tabular font-semibold text-foreground">{fmtMvr(p.amount)}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                    <span className="num">{formatDate(p.sentToFinanceAt)}</span>
                  </span>
                  {(p.gsrNumber != null || p.prNumber != null) && (
                    <Refs p={p} />
                  )}
                </div>
                {(() => {
                  // The vendor may confirm a different account when confirming the invoice
                  // (Award.confirmedBank*) — that's authoritative for payment; fall back to
                  // the vendor master account for older/unconfirmed awards.
                  const bankNumber = p.confirmedBankNumber || p.bankAccountNumber;
                  const bankName = p.confirmedBankName || p.bankAccountName;
                  if (!bankNumber && !bankName) return null;
                  return (
                    <div className="mt-1.5 inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md bg-muted/50 px-2 py-1 text-xs">
                      <Icon name="account_balance" size={15} className="shrink-0 text-foreground/40" />
                      {bankNumber && (
                        <span className="num font-semibold text-foreground" dir="ltr">{bankNumber}</span>
                      )}
                      {bankName && <span className="text-muted-foreground">{bankName}</span>}
                    </div>
                  );
                })()}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill paid={p.paid} />
                <DocumentsButton requestId={p.requestId} refNumber={p.refNumber} />
                <PaidToggle requestId={p.requestId} refNumber={p.refNumber} paid={p.paid} />
              </div>
            </div>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.payments.empty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
